/**
 * Shared Form Utilities
 *
 * Type guards and utility functions used across booking forms.
 */

import type {
  IBooking,
  IServiceCustomer,
  IServiceEmployee,
} from "@/services/types";

/**
 * Type guard to check if target is a customer
 *
 * Customers don't have `given_name` (employees do) or `b_type` (bookings do)
 */
export function isCustomerTarget(
  target: IServiceCustomer | IServiceEmployee | IBooking | null | undefined,
): target is IServiceCustomer {
  if (!target) return false;
  return !("given_name" in target) && !("b_type" in target);
}

/**
 * Type guard to check if target is an employee
 *
 * Employees have `given_name` property
 */
export function isEmployeeTarget(
  target: IServiceCustomer | IServiceEmployee | IBooking | null | undefined,
): target is IServiceEmployee {
  if (!target) return false;
  return "given_name" in target;
}

/**
 * Type guard to check if target is a booking
 *
 * Bookings have `b_type` property
 */
export function isBookingTarget(
  target: IServiceCustomer | IServiceEmployee | IBooking | null | undefined,
): target is IBooking {
  if (!target) return false;
  return "b_type" in target;
}

/**
 * Calculate median of an array of numbers (rounded to nearest 0.5)
 *
 * Used to determine default hours when editing multiple bookings
 */
export function calculateMedianHours(hours: number[]): number {
  if (hours.length === 0) return 8;
  const sorted = [...hours].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  const median =
    sorted.length % 2 !== 0 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
  return Math.round(median * 2) / 2;
}
